import { useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Bell, Users, FileText, MessageSquare, Crown, Check, CheckCircle2, BellOff, AtSign, CalendarClock, FileSearch, Bug, FileCheck } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useNotificationsStore, type NotificationType } from "@/stores/notifications-store"

const POLL_INTERVAL = 30_000 // 30 seconds

const typeIcons: Record<NotificationType, typeof Bell> = {
  member_added: Users,
  section_assigned: FileText,
  document_owner_assigned: FileText,
  review_requested: MessageSquare,
  review_approved: Check,
  changes_requested: MessageSquare,
  ownership_transferred: Crown,
  comment_added: MessageSquare,
  comment_reply: MessageSquare,
  comment_resolved: CheckCircle2,
  mention: AtSign,
  key_date_changed: CalendarClock,
  analysis_ready: FileSearch,
  feedback_resolved: Bug,
  draft_ready: FileCheck,
}

export function NotificationsBell() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotificationsStore()

  // Poll for unread count
  useEffect(() => {
    void fetchUnreadCount()
    const interval = setInterval(() => void fetchUnreadCount(), POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        void fetchNotifications()
      }
    },
    [fetchNotifications]
  )

  const handleNotificationClick = useCallback(
    (id: string, link: string | null, read: boolean) => {
      if (!read) {
        void markAsRead(id)
      }
      if (link) {
        void navigate(link)
      }
    },
    [markAsRead, navigate]
  )

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-medium">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={() => void markAllAsRead()}
              className="text-meta hover:text-foreground transition-colors"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-80 overflow-y-auto">
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <BellOff className="h-6 w-6 text-muted-foreground/50 mb-2" />
              <span className="text-sm text-muted-foreground">
                No notifications yet
              </span>
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = typeIcons[notification.type] ?? Bell
              return (
                <button
                  key={notification.id}
                  onClick={() =>
                    handleNotificationClick(
                      notification.id,
                      notification.link,
                      notification.read
                    )
                  }
                  className={cn(
                    "flex w-full items-start gap-3 px-4 py-2 text-left transition-colors hover:bg-muted/50",
                    !notification.read && "bg-muted/30"
                  )}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-xs leading-snug",
                        !notification.read && "font-medium"
                      )}
                    >
                      {notification.title}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="mt-2 flex-shrink-0">
                      <div className="h-2 w-2 rounded-full bg-[hsl(var(--ai-accent))]" />
                    </div>
                  )}
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
